import React from "react";
import { useTranslation } from "react-i18next";
import { scorePrediction, hasGamesPlayed } from "../../../utils/predictionScore";
import {
  PredictionsByDivision,
  StandingsByDivision,
  deviationClass,
  divisionLabel,
  formatSavedDate,
  slotsFromTeams,
  zoneColor,
} from "./predictShared";
import styles from "../Predict.module.css";

interface PredictionCardsProps {
  season: number;
  divisions: string[];
  standings: StandingsByDivision;
  predictions: PredictionsByDivision;
  /** Divisions saved in this visit, which read "Gemt lige nu". */
  justSaved?: Record<string, boolean>;
  /** Whose predictions these are: the empty-card copy differs. */
  owner: "self" | "other";
  locked: boolean;
  /** Opens a division in the editor; omit for a read-only view. */
  onOpen?: (division: string) => void;
}

/** Every division as a card: the pick, where each team stands now and how far off the pick is. */
const PredictionCards: React.FC<PredictionCardsProps> = ({
  season,
  divisions,
  standings,
  predictions,
  justSaved = {},
  owner,
  locked,
  onOpen,
}) => {
  const { t, i18n } = useTranslation("pages");
  const canOpen = !!onOpen && !locked;

  return (
    <div className={styles.cards}>
      {divisions.map((division) => {
        const table = standings[division] ?? [];
        const prediction = predictions[division];
        const scored = hasGamesPlayed(table);
        const score = prediction ? scorePrediction(prediction.teams, table) : null;
        const slots = prediction ? slotsFromTeams(prediction.teams, table) : [];
        const byId = new Map(table.map((s) => [s.team.teamId, s]));
        const label = divisionLabel(division);

        return (
          <section key={division} className={`${styles.panel} ${styles.card}`} aria-label={label}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>{label}</h2>
              {prediction ? (
                score && (
                  <span className={styles.cardScore}>
                    <span className={styles.cardScoreValue}>{score.score.toFixed(2)}</span>
                  </span>
                )
              ) : (
                <span className={styles.tag}>{t("predict.notFilled")}</span>
              )}
            </div>

            {prediction ? (
              <>
                <div className={`${styles.colHead} ${styles.cardCols}`}>
                  <span className={styles.center}>#</span>
                  <span style={{ paddingLeft: 12 }}>{t("predict.team")}</span>
                  <span className={styles.center}>{t("predict.now")}</span>
                  <span className={styles.center}>{scored ? t("predict.deviation") : ""}</span>
                </div>
                {slots.map((teamId, index) => {
                  const standing = teamId ? byId.get(teamId) : undefined;
                  if (!standing) return null;
                  const deviation = Math.abs(standing.rank - (index + 1));
                  return (
                    <div key={teamId} className={`${styles.cardRow} ${index % 2 === 1 ? styles.slotRowAlt : ""}`}>
                      <span className={`${styles.rankCell} ${styles.cardRank}`} style={{ background: zoneColor(season, division, index) }}>
                        {index + 1}
                      </span>
                      <span className={styles.cardTeam}>
                        {standing.team.teamLogo ? (
                          <img className={styles.logo} src={standing.team.teamLogo} alt="" />
                        ) : (
                          <span className={styles.logoFallback}>{standing.team.teamName.charAt(0).toUpperCase()}</span>
                        )}
                        <span className={styles.teamName}>{standing.team.teamName}</span>
                      </span>
                      <span className={styles.cardCur}>{scored ? `${standing.rank}.` : "–"}</span>
                      <span className={styles.cardChip}>
                        {scored && <span className={`${styles.devChip} ${deviationClass(deviation)}`}>{deviation}</span>}
                      </span>
                    </div>
                  );
                })}
                <div className={styles.cardFoot}>
                  <span>
                    {justSaved[division]
                      ? t("predict.statusSavedNow")
                      : t("predict.statusSaved", { date: formatSavedDate(prediction.updatedAt, i18n.language) })}
                  </span>
                  {canOpen && (
                    <button type="button" className={styles.ghostButton} onClick={() => onOpen!(division)}>
                      {t("predict.edit")}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className={styles.cardEmpty}>
                <span className={styles.cardEmptyTitle}>
                  {owner === "other"
                    ? t("predict.missingTitleOther", { division: label })
                    : locked
                      ? t("predict.missingTitleLocked", { division: label })
                      : t("predict.missingTitle", { division: label })}
                </span>
                <span className={styles.cardEmptyText}>{t("predict.missingText", { teams: table.length })}</span>
                {canOpen && (
                  <button type="button" className={styles.primaryButton} onClick={() => onOpen!(division)}>
                    {t("predict.makePrediction")}
                  </button>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default PredictionCards;
